<script setup lang="ts">
// Single-date filter. Wraps `<input type="date">` and emits the ISO
// `YYYY-MM-DD` string the browser exposes via `value`. Empty input
// (after the user clears it) emits `null` so authors can branch on
// "no filter".

import { computed, useId } from "vue";

import type { DateOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: string | null;
  opts?: DateOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: string | null): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text and points
// its `<label for>` at `controlId`.
const labelId = useId();
const controlId = useId();

// Print-mode display: the ISO date string verbatim, or a placeholder
// when the picker is empty. The board reader needs to see that the
// filter was deliberately unset, not just absent from the report.
const printValue = computed(() => props.value ?? "(not set)");

function onChange(e: Event): void {
  const raw = (e.target as HTMLInputElement).value;
  emit("change-target", props.id, raw === "" ? null : raw);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="date">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="date-label"
      :label="opts?.label"
      :description="opts?.description"
      :for-id="controlId"
      :label-id="labelId"
    />
    <input
      :id="controlId"
      type="date"
      :value="value ?? ''"
      :min="opts?.min"
      :max="opts?.max"
      @change="onChange"
    />
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.date {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .date {
    display: flex;
    width: 100%;
  }
}
.date-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
input[type="date"] {
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  cursor: pointer;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
input[type="date"]:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  input[type="date"] {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
